import { useCallback, useEffect, useRef, useState } from "react";
import type { HandoffAttemptLog } from "@/services/integrations/twilio/HandoffAttemptLog";
import {
  VoiceBridgeService,
  VoiceBridgeStatus,
  VoiceBridgeTranscriptMessage,
} from "@/services/integrations/twilio/VoiceBridgeService";

export interface UseVoiceBridgeResult {
  bridgeStatus: VoiceBridgeStatus;
  bridgeError: Error | null;
  muted: boolean;
  destinationLabel: string | null;
  /** True once the call is accepted; false when the visitor hung up during setup. */
  startBridge: (
    agentId: string,
    transcript?: VoiceBridgeTranscriptMessage[],
    log?: HandoffAttemptLog,
  ) => Promise<boolean>;
  endBridge: () => void;
  toggleMute: () => void;
}

// The service instance is created lazily, only when startBridge is called --
// this keeps @twilio/voice-sdk out of the bundle for the common case where a
// visitor never requests a human handoff.
export const useVoiceBridge = (): UseVoiceBridgeResult => {
  const [bridgeStatus, setBridgeStatus] = useState<VoiceBridgeStatus>("idle");
  const [bridgeError, setBridgeError] = useState<Error | null>(null);
  const [muted, setMuted] = useState(false);
  const [destinationLabel, setDestinationLabel] = useState<string | null>(null);
  const serviceRef = useRef<VoiceBridgeService | null>(null);

  useEffect(() => {
    return () => {
      serviceRef.current?.destroy();
      serviceRef.current = null;
    };
  }, []);

  // Once a call ends (either party hung up, or it failed to connect), briefly
  // show that status, then return to "idle" so the call bar disappears and
  // the "Talk to a human" button comes back instead of staying stuck showing
  // the ended/failed state forever.
  useEffect(() => {
    if (bridgeStatus !== "ended" && bridgeStatus !== "failed") return;
    const timer = window.setTimeout(() => {
      setBridgeStatus("idle");
      setBridgeError(null);
      setMuted(false);
      setDestinationLabel(null);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [bridgeStatus]);

  const startBridge = useCallback(
    async (
      agentId: string,
      transcript: VoiceBridgeTranscriptMessage[] = [],
      log?: HandoffAttemptLog,
    ): Promise<boolean> => {
      setBridgeError(null);
      setDestinationLabel(null);
      serviceRef.current?.destroy();

      const service = new VoiceBridgeService(agentId);
      serviceRef.current = service;

      service.setOnStatusChange((status) => {
        setBridgeStatus(status);
        if (status === "connecting") setDestinationLabel(service.getDestinationLabel());
      });
      service.setOnError(setBridgeError);

      return service.connect(transcript, log);
    },
    [],
  );

  const endBridge = useCallback((): void => {
    serviceRef.current?.disconnect();
  }, []);

  const toggleMute = useCallback((): void => {
    if (!serviceRef.current) return;
    const next = !serviceRef.current.isMuted();
    serviceRef.current.mute(next);
    setMuted(next);
  }, []);

  return { bridgeStatus, bridgeError, muted, destinationLabel, startBridge, endBridge, toggleMute };
};
